import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../hooks/useAuth';
import Swal from 'sweetalert2';

const SendMailModal = ({ isOpen = false, onClose, invoiceId, customerEmail, customerName }) => {
  const [formData, setFormData] = useState({
    smtpId: '',
    toEmail: customerEmail || '',
    toName: customerName || '',
    subject: '',
    body: '',
    templateId: '',
    useTemplate: false
  });

  const [previewMode, setPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState({ subject: '', body: '' });

  // Fetch SMTP options
  const { data: smtpOptions = [] } = useQuery({
    queryKey: ['smtp-options'],
    queryFn: async () => {
      const response = await api.get('/send-mail/smtp-options');
      return response.data;
    },
    enabled: Boolean(isOpen)
  });

  // Fetch template options
  const { data: templateOptions = [] } = useQuery({
    queryKey: ['template-options'],
    queryFn: async () => {
      const response = await api.get('/send-mail/template-options');
      return response.data;
    },
    enabled: Boolean(isOpen)
  });

  // Get customer suggestions
  const { data: customerSuggestions } = useQuery({
    queryKey: ['customer-suggestions', invoiceId],
    queryFn: async () => {
      const response = await api.get(`/send-mail/customer-suggestions/${invoiceId}`);
      return response.data;
    },
    enabled: Boolean(isOpen && invoiceId)
  });

  // Preview template mutation
  const previewMutation = useMutation({
    mutationFn: async ({ templateId, invoiceId }) => {
      const response = await api.post('/send-mail/preview-template', {
        templateId,
        invoiceId
      });
      return response.data;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setPreviewMode(true);
    }
  });

  // Send mail mutation
  const sendMailMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/send-mail', data);
      return response.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: 'success',
        title: 'Email Sent!',
        text: 'Invoice has been sent successfully.',
        timer: 2000,
        showConfirmButton: false
      });
      onClose();
    },
    onError: (error) => {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to send email',
        confirmButtonText: 'OK'
      });
    }
  });

  // Update form when customer suggestions change
  useEffect(() => {
    if (customerSuggestions) {
      setFormData(prev => ({
        ...prev,
        toEmail: customerSuggestions.email || prev.toEmail,
        toName: customerSuggestions.name || prev.toName
      }));
    }
  }, [customerSuggestions]);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle template selection
  const handleTemplateChange = (templateId) => {
    setFormData(prev => ({
      ...prev,
      templateId,
      useTemplate: !!templateId
    }));
  };

  // Preview template
  const handlePreview = () => {
    if (formData.templateId && invoiceId) {
      previewMutation.mutate({
        templateId: formData.templateId,
        invoiceId: invoiceId
      });
    }
  };

  // Send email
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic required fields
    const requiredFields = { smtpId: formData.smtpId, toEmail: formData.toEmail };
    
    // If not using template, subject and body are required
    if (!formData.useTemplate) {
      requiredFields.subject = formData.subject;
      requiredFields.body = formData.body;
    }
    
    // If using template, templateId is required
    if (formData.useTemplate && !formData.templateId) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please select a template.',
        confirmButtonText: 'OK'
      });
      return;
    }
    
    // Check if any required fields are missing
    const missingFields = Object.entries(requiredFields).filter(([key, value]) => !value);
    
    if (missingFields.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please fill in all required fields.',
        confirmButtonText: 'OK'
      });
      return;
    }

    const mailData = {
      smtpId: formData.smtpId,
      toEmail: formData.toEmail,
      toName: formData.toName,
      subject: formData.subject,
      body: formData.body,
      invoiceId: invoiceId
    };

    if (formData.useTemplate && formData.templateId) {
      mailData.templateId = formData.templateId;
    }

    sendMailMutation.mutate(mailData);
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        smtpId: '',
        toEmail: customerEmail || '',
        toName: customerName || '',
        subject: '',
        body: '',
        templateId: '',
        useTemplate: false
      });
      setPreviewMode(false);
    }
  }, [isOpen, customerEmail, customerName]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Send Invoice Email</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {previewMode ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Email Preview</h3>
                <button
                  onClick={() => setPreviewMode(false)}
                  className="btn btn-secondary btn-sm"
                >
                  Back to Edit
                </button>
              </div>
              
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject:
                  </label>
                  <div className="p-2 bg-white border rounded">
                    {previewData.subject}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Body:
                  </label>
                  <div 
                    className="p-4 bg-white border rounded min-h-[200px]"
                    dangerouslySetInnerHTML={{ __html: previewData.body }}
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setPreviewMode(false)}
                  className="btn btn-secondary"
                >
                  Back to Edit
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={sendMailMutation.isPending}
                  className="btn btn-primary"
                >
                  {sendMailMutation.isPending ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* SMTP Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SMTP Account *
                </label>
                <select
                  name="smtpId"
                  value={formData.smtpId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="">Select SMTP Account</option>
                  {smtpOptions.map((smtp) => (
                    <option key={smtp._id} value={smtp._id}>
                      {smtp.name} ({smtp.fromEmail}) {smtp.isDefault ? '(Default)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Recipient Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Email *
                  </label>
                  <input
                    type="email"
                    name="toEmail"
                    value={formData.toEmail}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Name
                  </label>
                  <input
                    type="text"
                    name="toName"
                    value={formData.toName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Use Template
                </label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="useTemplate"
                      checked={formData.useTemplate}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    Use email template
                  </label>
                </div>
                
                {formData.useTemplate && (
                  <div className="mt-2">
                    <select
                      value={formData.templateId}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select Template</option>
                      {templateOptions.map((template) => (
                        <option key={template._id} value={template._id}>
                          {template.name} {template.isDefault ? '(Default)' : ''}
                        </option>
                      ))}
                    </select>
                    
                    {formData.templateId && (
                      <button
                        type="button"
                        onClick={handlePreview}
                        disabled={previewMutation.isPending}
                        className="mt-2 btn btn-secondary btn-sm"
                      >
                        {previewMutation.isPending ? 'Loading...' : 'Preview Template'}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Subject - Only show when not using template */}
              {!formData.useTemplate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
              )}

              {/* Body - Only show when not using template */}
              {!formData.useTemplate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message Body *
                  </label>
                  <textarea
                    name="body"
                    value={formData.body}
                    onChange={handleChange}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendMailMutation.isPending}
                  className="btn btn-primary"
                >
                  {sendMailMutation.isPending ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendMailModal;
